/* global DEBUG */
import debounce from "lodash.debounce";
import { loadWasm } from "../clove/clove.js";
import getUserMedia from "./get-user-media";
import bufferAudio from "./recording/buffer-audio";
import encodeBuffer from "./recording/encode-buffer";
import { logTiming } from "@services/analytics";

const storage = (window.Storage = window.Storage || {});

const AudioContext = window.AudioContext || window.webkitAudioContext;

let audioBuffer;
let audioProcessor;
let audioSource;
let defaultDevice;
let stream;
let wasmLoaded = false;

/**
 * Activates users microphone and sets up
 * audio context for recording
 *
 * @returns {Promise}
 */
export function activateMicrophone() {
  storage.audioContext = new AudioContext();

  return getUserMedia({ audio: true })
    .then((mediaStream) => {
      stream = mediaStream;
      return getCurrentMicrophone();
    })
    .then((device) => {
      defaultDevice = device;

      window.navigator.mediaDevices.ondevicechange = debounce(
        onDeviceChange,
        100
      );

      prepareAudioContext(stream.clone());

      if (!wasmLoaded) {
        loadWasm().then(() => {
          wasmLoaded = true;
        });
      }
    });
}

/**
 * When a device is disconnected, check that microphone hasn't
 * changed, if so re-request microphone
 */
async function onDeviceChange() {
  const currentDevice = await getCurrentMicrophone();
  if (currentDevice !== defaultDevice) {
    await activateMicrophone();
  }
}

/**
 * Requests microphone again if stream disconnected,
 * e.g. when bluetooth headsets get connected or
 * disconnected and we need to switch to or from
 * internal microphone
 *
 * @returns {Promise<string>}
 */
function getCurrentMicrophone() {
  return window.navigator.mediaDevices.enumerateDevices().then((devices) => {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log(devices);
    }

    const audioDevices = devices.filter(
      (device) => device.kind === "audioinput"
    );

    if (!audioDevices) {
      return null;
    }

    const currentDevice =
      audioDevices.find(
        (device) =>
          device.deviceId === "default" ||
          device.label.toLowerCase().includes("default")
      ) || audioDevices[0];

    if (!currentDevice) {
      return null;
    }

    return currentDevice.id;
  });
}

/**
 * Clears audio buffer and prepares audio context
 * for new recording
 *
 * @param {MediaStream} newStream
 */
function prepareAudioContext(newStream) {
  stream = newStream;
  audioBuffer = new Float32Array(0);
  audioSource = storage.audioContext.createMediaStreamSource(stream);
  audioProcessor = storage.audioContext.createScriptProcessor(2048, 1, 1);
  audioSource.connect(audioProcessor);
  audioProcessor.connect(storage.audioContext.destination);
}

/**
 * Resets audio context
 */
async function resetAudioContext() {
  try {
    await storage.audioContext.suspend();
    audioProcessor.disconnect();
    audioSource.disconnect();
    // eslint-disable-next-line require-atomic-updates, no-undefined
    audioBuffer = audioProcessor = audioSource = undefined;
    prepareAudioContext(stream.clone());
  } catch (e) {
    if (!DEBUG) {
      try {
        const Sentry = await import("@sentry/browser");
        Sentry.captureException(e);
        // eslint-disable-next-line no-empty
      } catch (_) {}
    }
    // eslint-disable-next-line require-atomic-updates, no-undefined
    audioBuffer = audioProcessor = audioSource = undefined;
    await activateMicrophone();
  }
  return;
}

/**
 * Start recording users microphone
 */
export async function startRecording() {
  const started = Date.now();

  await resetAudioContext();

  if (storage.audioContext.state === "suspended") {
    await storage.audioContext.resume();
  }

  logTiming("recording", "start", Date.now() - started, "Microphone latency");

  audioProcessor.onaudioprocess = (e) => {
    const channelData = e.inputBuffer.getChannelData(0);
    audioBuffer = bufferAudio(channelData, audioBuffer);
  };
}

/**
 * Stop recording users microphone and return
 * encoded audio buffer
 *
 * @returns {Promise}
 */
export async function stopRecording() {
  const audio = encodeBuffer(audioBuffer, storage.audioContext.sampleRate);
  await resetAudioContext();
  return new Promise((resolve, reject) => {
    if (audio) {
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.log(`data:audio/wav;base64,${audio}`);
      }
      resolve(audio);
    } else {
      reject(new Error("No audio was captured"));
    }
  });
}
